import React from 'react';
import { FormattedDate, FormattedNumber, FormattedTime } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Text } from '@daedalus/atlas/Text';
import { Tooltip, TooltipPlacement } from '@daedalus/atlas/Tooltip';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { calculateNightlyRate } from '@daedalus/core/src/offer/business/offersMapping';
import { getDisplayPrice, getTotalPrice } from '@daedalus/core/src/price/business/price';
import { getFreezeSelectedContext, getOfferEligibility } from '../../modules/selector';
import { getDayDistance } from '../../utils/date';
interface Props {
  shouldShowPayNow: boolean;
}
const FirstRow = /*#__PURE__*/styled('div')({
  name: "FirstRow",
  class: "f8am4wf",
  propsAsIs: false
});
const FirstRowCell = /*#__PURE__*/styled('div')({
  name: "FirstRowCell",
  class: "f1y1fod1",
  propsAsIs: false
});
const TotalPriceRow = /*#__PURE__*/styled('div')({
  name: "TotalPriceRow",
  class: "t1gqmqzp",
  propsAsIs: false
});
const SecondRow = /*#__PURE__*/styled('div')({
  name: "SecondRow",
  class: "sbps1q",
  propsAsIs: false
});
const PayNow = /*#__PURE__*/styled('div')({
  name: "PayNow",
  class: "p14y1y6b",
  propsAsIs: false
});
const _exp11 = /*#__PURE__*/() => Text;
const ExpireDate = /*#__PURE__*/styled(_exp11())({
  name: "ExpireDate",
  class: "etl73sy",
  propsAsIs: true
});
const _exp17 = /*#__PURE__*/() => Tooltip;
const StyledTooltip = /*#__PURE__*/styled(_exp17())({
  name: "StyledTooltip",
  class: "s1gtmf06",
  propsAsIs: true
});
export const PriceSection = ({
  shouldShowPayNow
}: Props) => {
  const {
    response: offerCheckResponse,
    isLoading
  } = useSelector(getOfferEligibility);
  const {
    searchDetail
  } = useSelector(getFreezeSelectedContext);
  if (isLoading || !offerCheckResponse?.prices) return null;
  const {
    checkIn,
    checkOut
  } = searchDetail;
  const expireAt = offerCheckResponse?.expiresAt && new Date(offerCheckResponse?.expiresAt);
  const displayPrice = getDisplayPrice(offerCheckResponse?.prices);
  const numberOfNights = getDayDistance(checkIn, checkOut);
  const freezeTotalPrice = getTotalPrice(displayPrice);
  const freezePricePerNight = freezeTotalPrice && calculateNightlyRate(freezeTotalPrice, checkIn, checkOut);
  const displayPriceCurrency = displayPrice?.chargeable?.currency as string;
  const formattedNightlyPrice = <FormattedNumber value={freezePricePerNight} currency={displayPriceCurrency} style="currency" />;
  const freezeExpires = <FormattedDate value={expireAt} month="short" weekday="short" day="2-digit" />;
  const toolTipContent = <FormattedMessageWrapper id="dealFreeze.tooltipContent" defaultMessage="Room price and availability guaranteed until <br></br> {date}, at {time}" values={{
    date: <FormattedDate value={expireAt} weekday={'long'} year={'numeric'} month="long" day="2-digit" />,
    time: <FormattedTime value={expireAt} hour12={false} hour="numeric" minute="2-digit" timeZone="CET" timeZoneName="short" />,
    br: () => <br />
  }} />;
  return <>
      <FirstRow>
        <FirstRowCell>
          <Text variant="bodyS" colorPath="content.neutral.c600">
            <FormattedMessageWrapper id="dealFreeze.holdingRoomUntil" defaultMessage="Holding room until" />
          </Text>
          <StyledTooltip content={toolTipContent} placement={TooltipPlacement.TopStart}>
            <ExpireDate variant="titleM">{freezeExpires}</ExpireDate>
          </StyledTooltip>
        </FirstRowCell>
        <FirstRowCell>
          <Text variant="bodyS" colorPath="content.neutral.c600">
            <FormattedMessageWrapper id="dealFreeze.guaranteedPrice" defaultMessage="Guaranteed price" />
          </Text>
          {freezePricePerNight && <ContentWrapper paddingTop="s100">
              <Flex.Row alignItems="center" gap="s100">
                <Text variant="labelM">{formattedNightlyPrice}</Text>
                <Text variant="bodyS">
                  <FormattedMessageWrapper id="perNight" defaultMessage="/ night" />
                </Text>
              </Flex.Row>
            </ContentWrapper>}
        </FirstRowCell>
      </FirstRow>
      {freezeTotalPrice && <TotalPriceRow>
          <Flex.Row alignItems="center" justifyContent="space-between">
            <Text variant="titleM">
              <FormattedMessageWrapper id="dealFreeze.totalPriceOfStay" defaultMessage="Total price of stay:" />
            </Text>
            <Text variant="labelM">
              <FormattedNumber value={freezeTotalPrice} currency={displayPriceCurrency} style="currency" />
            </Text>
          </Flex.Row>
          {freezePricePerNight && numberOfNights > 1 && <Text variant="bodyS" colorPath="content.neutral.c600">
              <FormattedMessageWrapper id="dealFreeze.nights" defaultMessage="({price} x {numberOfNights, plural, one {1 night} other {# nights}})" values={{
          numberOfNights,
          price: formattedNightlyPrice
        }} />
            </Text>}
        </TotalPriceRow>}
      {shouldShowPayNow && <SecondRow>
          <PayNow>
            <Text variant="titleM">
              <FormattedMessageWrapper id="dealFreeze.costToFreeze" defaultMessage="Cost to freeze:" />
            </Text>
            <Text variant="titleL" colorPath="content.special.c600">
              <FormattedNumber value={0} currency={displayPriceCurrency} style="currency" />
            </Text>
          </PayNow>
          <Text variant="bodyS" colorPath="content.neutral.c600">
            <FormattedMessageWrapper id="dealFreeze.noPaymentRequired" defaultMessage="Secure up to three deals at once – totally free" />
          </Text>
        </SecondRow>}
    </>;
};

require("./PriceSection.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PriceSection.tsx");